import { SHAPES } from './constants'
import type { CellRuntime, NormalizedCell } from './types'

import type { CellConfig, Direction, TrackShape } from '@config'

function isTrackShape(value: string): value is TrackShape {
  return value in SHAPES
}

function normalizeShapes(trackDef: TrackShape | TrackShape[]): TrackShape[] {
  if (typeof trackDef === 'string') {
    return isTrackShape(trackDef) ? [trackDef] : []
  }

  if (!Array.isArray(trackDef)) return []
  return trackDef.filter(isTrackShape)
}

export function normalizeGridCell(raw: CellConfig): NormalizedCell | null {
  if (raw == null) return null

  let trackDef: TrackShape | TrackShape[]
  let decoration = false
  let exit: Direction | undefined
  let entrance: Direction | undefined

  if (typeof raw === 'object' && !Array.isArray(raw) && 'track' in raw) {
    trackDef = raw.track
    decoration = !!raw.decoration
    exit = raw.exit
    entrance = raw.entrance
  } else {
    trackDef = raw as TrackShape | TrackShape[]
  }

  const shapes = normalizeShapes(trackDef)
  if (shapes.length === 0) return null

  if (shapes.length === 1) {
    const shape = shapes[0]
    if (!shape) return null

    const type = SHAPES[shape].ports.length === 1 ? 'bumper' : 'track'

    return {
      type,
      shapes,
      active: 0,
      decoration: decoration || type === 'bumper',
      exit,
      entrance,
    }
  }

  return { type: 'switch', shapes, active: 0, decoration }
}

export function getCellRuntime(cell: NormalizedCell): CellRuntime | null {
  if (cell.shapes.length === 0) return null

  const activeIndex = Math.max(0, Math.min(cell.active, cell.shapes.length - 1))
  const activeShape = cell.shapes[activeIndex]
  if (!activeShape) return null

  const ports = Array.from(new Set(cell.shapes.flatMap(shape => SHAPES[shape].ports)))

  return {
    type: cell.type,
    ports,
    links: [...SHAPES[activeShape].links],
    activeShape,
  }
}
